import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Progress,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";

import Card from "components/card/Card";
import IconBox from "components/icons/IconBox";

import { ParameterDetail } from "./ParameterDetail";
import { InputPremise } from "./Premises";
import { getIcon } from "components/icons/Icons";
import {
  formatPremiseMetric,
  getOriginBadgeColor,
  getOriginLabel,
  getSessionTypeLabel,
  isPremiseConfigured,
  type PremiseLayout,
} from "./premiseDisplayUtils";

export const ParameterInfo = (props: {
  premise: InputPremise;
  layout?: PremiseLayout;
  [x: string]: any;
}) => {
  const {
    id,
    name,
    description,
    origin,
    lastUpdate,
    lastModifiedBy,
    value,
    downloadable,
    summarizingString,
    displayIcon,
    isConfigurable,
    type,
  } = props.premise;
  const { productOptions, reloader, layout = "card" } = props;

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "gray.400");
  const iconBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "white");
  const listBg = useColorModeValue("white", "navy.800");
  const listBorder = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
  const listHoverBorder = useColorModeValue("brand.200", "brand.400");
  const listHoverShadow = useColorModeValue(
    "0 8px 24px rgba(112, 144, 176, 0.12)",
    "0 8px 24px rgba(0, 0, 0, 0.25)"
  );
  const progressColor = isPremiseConfigured(props.premise) ? "green" : "red";

  const originLabel = getOriginLabel(origin);
  const metric = formatPremiseMetric(props.premise);
  const configured = isPremiseConfigured(props.premise);
  const sessionSuffix =
    origin === "user_input" ? ` · ${getSessionTypeLabel(type)}` : "";

  const detailProps = {
    lastModifiedBy,
    lastUpdate,
    id,
    name,
    nameString: description,
    origin,
    reloader,
    originString: originLabel,
    isDownloadable: downloadable,
    isConfigurable: isConfigurable && type !== "external",
    productOptions,
    actionVariant: layout === "list" ? ("inline" as const) : ("menu" as const),
  };

  if (layout === "list") {
    return (
      <Box
        w="100%"
        p={4}
        bg={listBg}
        borderWidth="1px"
        borderColor={listBorder}
        borderRadius="16px"
        transition="box-shadow 0.2s ease, border-color 0.2s ease"
        _hover={{
          borderColor: listHoverBorder,
          boxShadow: listHoverShadow,
        }}
      >
        <Flex align="flex-start" gap={3}>
          <IconBox
            flexShrink={0}
            h="48px"
            w="48px"
            icon={
              <Icon
                as={getIcon(displayIcon)}
                color={iconColor}
                h="24px"
                w="24px"
              />
            }
            bg={iconBg}
          />
          <VStack align="stretch" flex="1" spacing={2} minW={0}>
            <Flex
              align="flex-start"
              justify="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <Box flex="1" minW={0}>
                <Text
                  color={textColorPrimary}
                  fontWeight="700"
                  fontSize="md"
                  lineHeight="short"
                  noOfLines={2}
                >
                  {description}
                </Text>
                <HStack mt={2} spacing={2} flexWrap="wrap">
                  <Badge
                    colorScheme={getOriginBadgeColor(origin)}
                    variant="subtle"
                    fontSize="xs"
                    borderRadius="md"
                  >
                    {originLabel}
                    {sessionSuffix}
                  </Badge>
                  <Badge
                    colorScheme={configured ? "green" : "red"}
                    variant="subtle"
                    fontSize="xs"
                    borderRadius="md"
                  >
                    {configured ? "Listo" : "Pendiente"}
                  </Badge>
                </HStack>
              </Box>
              <ParameterDetail {...detailProps} ms={0} />
            </Flex>
            <Text
              color={textColorPrimary}
              fontSize="sm"
              fontWeight="600"
              wordBreak="break-word"
            >
              {metric}
            </Text>
            {summarizingString && value != null && value !== "" ? (
              <Text color={textColorSecondary} fontSize="xs">
                Valor de filtro: {value === "all" ? "Todos" : String(value)}
              </Text>
            ) : null}
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Card alignItems="center" p="15px" h="100%">
      <Flex w="100%">
        <ParameterDetail {...detailProps} ms="auto" />
      </Flex>
      <IconBox
        mx="auto"
        mt="-10px"
        h="80px"
        w="80px"
        icon={
          <Icon as={getIcon(displayIcon)} color={iconColor} h="40px" w="40px" />
        }
        bg={iconBg}
      />
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="md"
        mt="14px"
        textAlign="center"
        px={2}
      >
        {description}
      </Text>
      <Badge
        mt={2}
        colorScheme={getOriginBadgeColor(origin)}
        variant="subtle"
        fontSize="xs"
      >
        {originLabel}
        {sessionSuffix}
      </Badge>
      <Text
        color={textColorSecondary}
        fontSize="sm"
        mt={3}
        mb={2}
        textAlign="center"
        px={2}
        wordBreak="break-word"
      >
        {metric}
      </Text>
      <Box w="100%" mt="auto">
        <Progress
          value={configured ? 100 : 35}
          w="100%"
          size="xs"
          borderRadius="full"
          colorScheme={progressColor}
        />
      </Box>
    </Card>
  );
};
